import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle, AlertCircle, Loader2, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token provided.');
      return;
    }

    // Prevent double execution (React StrictMode / page refresh race)
    if (calledRef.current) return;
    calledRef.current = true;

    const verifyEmail = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/verify-email?token=${token}`);
        if (res.ok) {
          setStatus('success');
        } else {
          const data = await res.json();
          setStatus('error');
          setErrorMessage(data.detail || 'Verification failed.');
        }
      } catch {
        setStatus('error');
        setErrorMessage('Connection error. Please try again.');
      }
    };

    verifyEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 pt-20">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
            {status === 'loading' && (
              <div data-testid="verify-loading">
                <Loader2 className="w-12 h-12 animate-spin text-[#1a237e] mx-auto mb-4" />
                <h1 className="text-xl font-bold text-[#0d1442] mb-2">{t('auth.verifyEmail')}</h1>
                <p className="text-slate-500">Verifying your email...</p>
              </div>
            )}

            {status === 'success' && (
              <div data-testid="verify-success">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-xl font-bold text-[#0d1442] mb-2">{t('auth.verificationSuccess')}</h1>
                <Link
                  data-testid="verify-login-link"
                  to="/login"
                  className="inline-block mt-4 px-6 py-3 bg-[#1a237e] text-white font-medium rounded-lg hover:bg-[#0d1442] transition-colors"
                >
                  {t('auth.signIn')}
                </Link>
              </div>
            )}

            {status === 'error' && (
              <div data-testid="verify-error">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h1 className="text-xl font-bold text-[#0d1442] mb-2">{t('auth.verificationFailed')}</h1>
                <p className="text-slate-500 mb-4">{errorMessage}</p>
                <div className="flex flex-col gap-2">
                  <Link to="/login" className="text-[#1a237e] font-medium hover:underline text-sm">
                    {t('auth.backToLogin')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyEmailPage;
